/**
 * Guards navigation to package “success” routes when LDS returned an error or booking is incomplete.
 * Does not change payment capture, pricing, or LDS XML — FE routing/UX only.
 */

import {
  getPackageOrderProfilePath,
  getPackageOrderSuccessPath,
  isBankTransferAwaitingPayment,
  isLdsOrderResponseFailedForNavigation,
  isLdsProposalEntity,
  isPackageProfileBookingSuccessRouteAllowed,
  isPaidOrder,
  isReservationPresent,
  ldsOrderResponseBodyFailLike,
} from "./ldsOrderStatus";

// DEV/STAGING TEST ONLY — do not enable in production. Gated by `import.meta.env.PROD` + localStorage FORCE_LDS_ERROR.
/** @see applyDevForcedLdsErrorIfEnabled */
export const LDS_FORCE_ERROR_STORAGE_KEY = "FORCE_LDS_ERROR";

const _DEV_FORCE_LDS_MSG = "DEV forced LDS error";

/**
 * True when non-production build and `localStorage.FORCE_LDS_ERROR === "1"`.
 * Production: always false (ignores localStorage).
 */
export function isDevForcedLdsErrorActive() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) {
      return false;
    }
    if (typeof localStorage === "undefined" || !localStorage?.getItem) {
      return false;
    }
    return localStorage.getItem(LDS_FORCE_ERROR_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function _summarizeOrderForForceTrace(order) {
  if (!order || typeof order !== "object") return null;
  return {
    id: order.id ?? order.ID ?? null,
    paid: order.paid ?? null,
    hasOrderResponse: Boolean(order.order_response),
    responseStatus:
      order.order_response?.status != null ? String(order.order_response.status) : null,
    opStatus:
      order.order_response?.opStatus?.status != null
        ? String(order.order_response.opStatus.status)
        : null,
  };
}

/**
 * When {@link isDevForcedLdsErrorActive}, replaces the order payload with an LDS-fail-shaped row so
 * {@link shouldNavigateToLdsSuccess} blocks. No-op in production.
 * @param {object} ctx
 * @returns {object}
 */
function applyDevForcedLdsErrorIfEnabled(ctx) {
  if (!ctx || typeof ctx !== "object") return ctx;
  if (!isDevForcedLdsErrorActive()) return ctx;

  const source = ctx.source ?? "unknown";
  const rawOrder =
    ctx.order ??
    ctx.apiData ??
    ctx.responseData ??
    ctx?.axiosResponse?.data ??
    null;
  const base =
    rawOrder && typeof rawOrder === "object" && !Array.isArray(rawOrder)
      ? { ...rawOrder }
      : {};
  delete base.order_response;

  const forcedOrder = {
    ...base,
    ok: false,
    status: "ERROR",
    message: _DEV_FORCE_LDS_MSG,
    // Use string so `_topLevelError` picks it up (`error: true` alone is skipped — non-string).
    error: _DEV_FORCE_LDS_MSG,
    order_response: {
      status: "ERROR",
      message: _DEV_FORCE_LDS_MSG,
      opStatus: {
        status: "ERROR",
        message: _DEV_FORCE_LDS_MSG,
      },
    },
  };

  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD !== true) {
      // eslint-disable-next-line no-console
      console.info("[LDS_FORCE_ERROR_TEST_TRACE]", {
        source,
        enabled: true,
        originalResponseSummary: _summarizeOrderForForceTrace(
          rawOrder && typeof rawOrder === "object" ? rawOrder : null
        ),
        forcedResponseSummary: _summarizeOrderForForceTrace(forcedOrder),
        currentPath: typeof window !== "undefined" ? window.location.pathname || "" : "",
      });
    }
  } catch {
    /* ignore */
  }

  return {
    ...ctx,
    order: forcedOrder,
    _devLdsForceApplied: true,
  };
}

function _currentPath() {
  return typeof window !== "undefined" ? window.location.pathname || "" : "";
}

function _extractOrderPayload(ctx) {
  if (!ctx || typeof ctx !== "object") return null;
  if (ctx.error) return null;
  const raw =
    ctx.order ??
    ctx.apiData ??
    ctx.responseData ??
    ctx.axiosResponse?.data ??
    null;
  if (!raw || typeof raw !== "object") return null;
  return raw;
}

function _responseHttpOk(ctx) {
  if (!ctx?.axiosResponse || typeof ctx.axiosResponse !== "object") return true;
  const s = ctx.axiosResponse.status;
  return s == null || (s >= 200 && s < 400);
}

function _topLevelError(order) {
  if (!order || typeof order !== "object") return null;
  const e = order.error ?? order.exception ?? order.message;
  if (typeof e === "string" && e.trim()) return e.trim();
  return null;
}

function _opStatusTuple(order) {
  const op = order?.order_response?.opStatus;
  if (!op || typeof op !== "object") return { status: null, message: null };
  return {
    status: op.status != null ? String(op.status).trim() : null,
    message: op.message != null ? String(op.message).trim() : null,
  };
}

function _bookingRefHint(order) {
  const res = order?.order_response?.reservation;
  if (res && typeof res === "object") {
    const p =
      res.supplierPNR ?? res.bookingRef ?? res.booking_ref ?? res.pnr ?? null;
    if (p != null && String(p).trim()) return String(p).trim();
  }
  return order?.booking_ref != null && String(order.booking_ref).trim()
    ? String(order.booking_ref).trim()
    : null;
}

/**
 * After {@link shouldNavigateToLdsSuccess} returns allow=false (e.g. customer bank save with real OK order
 * in React state), effects such as post-payment auto-nav may still run with the same row and would
 * navigate unless suppressed for a short window.
 */
let _packageProfileSuccessNavArmUntil = 0;
let _packageProfileSuccessNavArmReason = null;

/** @param {string} [reason] */
export function armPackageProfileSuccessNavSuppression(reason, ttlMs = 120000) {
  try {
    const ms = Number(ttlMs) > 0 ? Number(ttlMs) : 120000;
    _packageProfileSuccessNavArmUntil = Date.now() + ms;
    _packageProfileSuccessNavArmReason = reason != null ? String(reason) : "guard_block";
  } catch {
    /* ignore */
  }
}

export function clearPackageProfileSuccessNavSuppression() {
  _packageProfileSuccessNavArmUntil = 0;
  _packageProfileSuccessNavArmReason = null;
}

export function isPackageProfileSuccessNavSuppressed() {
  return Date.now() < _packageProfileSuccessNavArmUntil;
}

export function peekPackageProfileSuccessNavSuppressionReason() {
  return _packageProfileSuccessNavArmReason;
}

function _pathLooksLikePackageOrderSuccess(targetPath) {
  if (targetPath == null) return false;
  return String(targetPath).includes("/success");
}

/**
 * console.trace before any navigation toward `/packages/profile/:id/success` (non-PROD).
 * @param {string} source
 * @param {string|null|undefined} targetPath
 */
export function traceHardLdsPackageSuccessNav(source, targetPath) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) return;
    // eslint-disable-next-line no-console
    console.trace("[LDS_SUCCESS_NAV_ATTEMPT_TRACE_HARD]", { source, targetPath: targetPath ?? null });
  } catch {
    /* ignore */
  }
}

/**
 * Blocks programmatic navigation to package success when suppression is armed (e.g. after LDS guard modal).
 * @returns {boolean} true if navigation must be skipped
 */
export function hardBlockLdsPackageSuccessNavigationIfNeeded(source, targetPath) {
  if (!_pathLooksLikePackageOrderSuccess(targetPath)) return false;
  if (!isPackageProfileSuccessNavSuppressed()) return false;
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) return true;
    // eslint-disable-next-line no-console
    console.warn("[LDS_SUCCESS_NAV_HARD_BLOCK]", {
      source,
      targetPath,
      suppressionReason: peekPackageProfileSuccessNavSuppressionReason(),
    });
  } catch {
    /* ignore */
  }
  return true;
}

/**
 * Always-on observability for any code path that tries to reach a package success route.
 * @param {{
 *   source?: string,
 *   targetPath?: string|null,
 *   currentPath?: string,
 *   suppressionActive?: boolean,
 *   suppressionReason?: string|null,
 *   isNavigatingToSuccess?: boolean|null,
 *   isPaymentSuccess?: boolean|null,
 *   ourSavedLDSOrderId?: string|number|null,
 *   stack?: string,
 * }} payload
 */
export function logLdsSuccessNavAttemptTrace(payload) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) return;
    const stack = payload?.stack ?? new Error().stack;
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_NAV_ATTEMPT_TRACE]", { ...payload, stack });
  } catch {
    /* ignore */
  }
}

/**
 * Hard gate: if package success navigation is suppressed, log attempt + bypass and do not run `execNavigate`.
 * @param {{
 *   source: string,
 *   targetPath?: string|null,
 *   isNavigatingToSuccess?: boolean|null,
 *   isPaymentSuccess?: boolean|null,
 *   ourSavedLDSOrderId?: string|number|null,
 *   execNavigate: () => void,
 * }} p
 * @returns {boolean} true if navigation ran
 */
export function guardAndLogPackageProfileSuccessNavigation(p) {
  const tp = p.targetPath ?? null;
  traceHardLdsPackageSuccessNav(p.source, tp);
  if (hardBlockLdsPackageSuccessNavigationIfNeeded(p.source, tp)) {
    logLdsSuccessNavBypassTrace({
      source: p.source,
      navAllow: false,
      afterModalReturnReached: false,
      nextNavigateAttempt: tp,
      targetPath: tp,
      reason: peekPackageProfileSuccessNavSuppressionReason() || "hard_block_success_while_suppressed",
    });
    return false;
  }
  const currentPath = _currentPath();
  const suppressionActive = isPackageProfileSuccessNavSuppressed();
  const suppressionReason = peekPackageProfileSuccessNavSuppressionReason();
  logLdsSuccessNavAttemptTrace({
    source: p.source,
    targetPath: tp,
    currentPath,
    suppressionActive,
    suppressionReason,
    isNavigatingToSuccess: p.isNavigatingToSuccess ?? null,
    isPaymentSuccess: p.isPaymentSuccess ?? null,
    ourSavedLDSOrderId: p.ourSavedLDSOrderId ?? null,
    stack: new Error().stack,
  });
  if (suppressionActive) {
    logLdsSuccessNavBypassTrace({
      source: p.source,
      navAllow: false,
      afterModalReturnReached: false,
      nextNavigateAttempt: tp,
      targetPath: tp,
      reason: suppressionReason || "suppression_active_hard_gate",
    });
    return false;
  }
  p.execNavigate();
  return true;
}

/**
 * @param {{ source?: string, navAllow?: boolean, afterModalReturnReached?: boolean, nextNavigateAttempt?: string|null, targetPath?: string|null, reason?: string|null, decision?: string|null }} payload
 */
export function logLdsSuccessNavBypassTrace(payload) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) return;
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_NAV_BYPASS_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

function logSuccessNavGuardTrace(payload) {
  try {
    const isProd = typeof import.meta !== "undefined" && import.meta.env?.PROD === true;
    if (isProd) return;
    const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV === true;
    const forceTrace = payload?.devForcedLdsError === true;
    if (!isDev && !forceTrace) return;
    const out =
      forceTrace && payload?.reason
        ? { ...payload, reason: `${payload.reason} (DEV forced LDS error)` }
        : payload;
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_NAV_GUARD_TRACE]", out);
  } catch {
    /* ignore */
  }
}

/**
 * Canonical route decision trace (non-PROD). One object per routing decision toward package profile /success.
 * @param {Record<string, unknown>} payload
 */
export function logLdsSuccessRouteDecisionTrace(payload) {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.PROD === true) return;
    // eslint-disable-next-line no-console
    console.info("[LDS_SUCCESS_ROUTE_DECISION_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object} p
 * @param {string} p.source
 * @param {string|number|null|undefined} p.orderId
 * @param {string|null|undefined} p.targetPath
 * @param {string|null|undefined} [p.paymentMethod]
 * @param {boolean} [p.isBankTransfer]
 * @param {boolean} [p.isLater]
 * @param {boolean} [p.isProposal]
 * @param {boolean} [p.isAwaitingBankTransfer]
 * @param {string|null|undefined} [p.completionState]
 * @param {string|null|undefined} [p.orderResponseStatus]
 * @param {boolean} [p.shouldUseSuccessRoute]
 * @param {string|null|undefined} [p.reason]
 */
export function buildLdsSuccessRouteDecisionTracePayload(p) {
  return {
    source: p.source ?? "unknown",
    orderId: p.orderId ?? null,
    targetPath: p.targetPath ?? null,
    paymentMethod: p.paymentMethod ?? null,
    isBankTransfer: Boolean(p.isBankTransfer),
    isLater: Boolean(p.isLater),
    isProposal: Boolean(p.isProposal),
    isAwaitingBankTransfer: Boolean(p.isAwaitingBankTransfer),
    completionState: p.completionState ?? null,
    orderResponseStatus: p.orderResponseStatus ?? null,
    shouldUseSuccessRoute: Boolean(p.shouldUseSuccessRoute),
    reason: p.reason ?? null,
  };
}

/**
 * User-facing remediation for LDS / save failures (Hebrew).
 * @param {{ order?: object|null, source?: string, paidContext?: boolean }} input
 */
export function resolveLdsErrorAction(input) {
  const order = input?.order;
  const paidContext =
    input?.paidContext ?? (order && isPaidOrder(order)) ?? false;
  const { status: ldsStatus, message: ldsMsg } = _opStatusTuple(order);
  const rawUpper = String(ldsStatus || "").toUpperCase();
  const msgLower = String(ldsMsg || "").toLowerCase();
  const combined = `${msgLower} ${_topLevelError(order) || ""}`.toLowerCase();

  let targetSection = "order_details";
  let severity = "error";
  let title = "לא ניתן להשלים את הפעולה";
  let messageHe =
    ldsMsg ||
    _topLevelError(order) ||
    "אירעה שגיאה בתקשורת עם ספק החבילה. בדקו את הפרטים ונסו שוב, או פנו לתמיכה.";
  let actionLabel = "הבנתי";

  if (
    (combined.includes("deal") && combined.includes("not")) ||
    combined.includes("לא נמצא") ||
    (combined.includes("package") && combined.includes("expir"))
  ) {
    targetSection = "rooms";
    title = "החבילה או החדר אינם זמינים";
    messageHe =
      "ייתכן שהחבילה פגה או שהחדר אינו זמין. חזרו לבחירת חדרים או לחיפוש מחדש.";
  } else if (
    rawUpper === "INVALID_FORM" ||
    rawUpper === "INVALID_PARAM" ||
    (isLdsOrderResponseFailedForNavigation(order) &&
      /pax|passenger|child|adult/i.test(combined))
  ) {
    targetSection = "passengers";
    title = "נדרש תיקון בפרטי הנוסעים";
    messageHe =
      ldsMsg ||
      "בדקו שכל שדות הנוסעים והתאריכים נכונים בהתאם להרכב החדר, ונסו שוב.";
  } else if (
    paidContext &&
    isLdsOrderResponseFailedForNavigation(order) &&
    !isReservationPresent(order)
  ) {
    targetSection = "payment_pending";
    severity = "warning";
    title = "התשלום נקלט — נדרש טיפול";
    messageHe =
      "התשלום נקלט, אך אישור הספק לא הושלם. ההזמנה דורשת טיפול ידני. צוות המערכת יעדכן בהקדם או פנו לתמיכה.";
    actionLabel = "חזרה להזמנה";
  }

  return {
    title,
    message: messageHe,
    targetSection,
    actionLabel,
    severity,
    ldsStatus,
    ldsMessage: ldsMsg,
  };
}

/**
 * @param {{
 *   source?: string,
 *   targetPath?: string|null,
 *   order?: object|null,
 *   apiData?: object|null,
 *   responseData?: object|null,
 *   axiosResponse?: { data?: object, status?: number }|null,
 *   error?: Error|null,
 * }} ctx
 * @returns {{
 *   allow: boolean,
 *   allowSuccessRoute: boolean,
 *   continueFlow: boolean,
 *   decision: string,
 *   reason: string,
 *   completionState: string,
 *   targetSection: string|null,
 *   trace: object,
 *   ldsErrorAction: ReturnType<typeof resolveLdsErrorAction>|null,
 * }}
 */
export function shouldNavigateToLdsSuccess(ctx = {}) {
  const ctxIn = applyDevForcedLdsErrorIfEnabled(ctx);
  const source = ctxIn.source ?? "unknown";
  const targetPath = ctxIn.targetPath ?? null;
  const currentPath = _currentPath();
  const err = ctxIn.error ?? null;
  const order = _extractOrderPayload(ctxIn);
  const devForcedLdsError = Boolean(ctxIn._devLdsForceApplied);

  let decision = "block";
  let reason = "unknown";
  let completionState = "none";
  let allow = false;
  let ldsErrorAction = null;

  const traceBase = {
    source,
    currentPath,
    targetPath,
    responseOk: _responseHttpOk(ctxIn),
    hasError: Boolean(err || _topLevelError(order)),
    ldsStatus: _opStatusTuple(order).status,
    ldsErrorCode: order?.order_response?.opStatus?.code ?? null,
    ldsErrorMessage: _opStatusTuple(order).message,
    bookingRef: _bookingRefHint(order),
    completionState,
    decision: "block",
    reason: "init",
    targetSection: null,
    devForcedLdsError,
  };

  if (err) {
    decision = "block";
    reason = "exception";
    ldsErrorAction = resolveLdsErrorAction({
      order: order || {},
      source,
      paidContext: false,
    });
    traceBase.targetSection = ldsErrorAction.targetSection;
    const trace = { ...traceBase, decision, reason, completionState };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  if (!_responseHttpOk(ctxIn)) {
    decision = "block";
    reason = "http_error";
    ldsErrorAction = resolveLdsErrorAction({ order: order || {}, source });
    traceBase.targetSection = ldsErrorAction.targetSection;
    const trace = { ...traceBase, decision, reason, completionState };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  if (!order || typeof order !== "object") {
    decision = "block";
    reason = "missing_order_payload";
    ldsErrorAction = resolveLdsErrorAction({ order: {}, source });
    const trace = {
      ...traceBase,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
    };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  const topErr = _topLevelError(order);
  if (topErr) {
    decision = "block";
    reason = "payload_error_field";
    completionState = "error";
    ldsErrorAction = resolveLdsErrorAction({ order, source });
    const trace = {
      ...traceBase,
      hasError: true,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
    };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  if (ldsOrderResponseBodyFailLike(order.order_response) && !isReservationPresent(order)) {
    decision = "block";
    reason = "lds_order_response_fail";
    completionState = isPaidOrder(order) ? "paid_lds_failed" : "lds_failed";
    ldsErrorAction = resolveLdsErrorAction({
      order,
      source,
      paidContext: isPaidOrder(order),
    });
    const trace = {
      ...traceBase,
      hasError: true,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
    };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  const id = order.id ?? order.ID;
  if (id == null || String(id).trim() === "") {
    decision = "block";
    reason = "missing_order_id";
    ldsErrorAction = resolveLdsErrorAction({ order, source });
    const trace = {
      ...traceBase,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
    };
    logSuccessNavGuardTrace(trace);
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState,
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  const okBankPending = isBankTransferAwaitingPayment(order);
  const allowSuccessRoute = isPackageProfileBookingSuccessRouteAllowed(order);
  const isProposal = isLdsProposalEntity(order);
  const orderResponseStatus =
    order?.order_response?.status != null
      ? String(order.order_response.status)
      : _opStatusTuple(order).status;
  const profilePath = getPackageOrderProfilePath(id);
  let allowSuccessRouteFlag = false;
  let continueFlow = false;

  if (allowSuccessRoute) {
    completionState = "booking_confirmed_supplier_record";
    decision = "allow_success_route";
    reason = "confirmed_booking_eligible_for_success_url";
    allow = true;
    allowSuccessRouteFlag = true;
    continueFlow = true;
  } else if (okBankPending) {
    completionState = "bank_transfer_pending";
    decision = "use_profile_route";
    reason = "awaiting_bank_payment";
    allow = false;
    allowSuccessRouteFlag = false;
    continueFlow = true;
    ldsErrorAction = null;
  } else if (isProposal) {
    completionState = "proposal";
    decision = "use_profile_route";
    reason = "proposal_entity";
    allow = false;
    allowSuccessRouteFlag = false;
    continueFlow = true;
    ldsErrorAction = null;
  } else if (
    isPaidOrder(order) &&
    !isReservationPresent(order) &&
    !isLdsOrderResponseFailedForNavigation(order)
  ) {
    completionState = "paid_awaiting_supplier_record";
    decision = "use_profile_route";
    reason = "paid_without_reservation_yet";
    allow = false;
    allowSuccessRouteFlag = false;
    continueFlow = true;
    ldsErrorAction = null;
  } else {
    decision = "block";
    reason = "not_eligible_success_route";
    ldsErrorAction = resolveLdsErrorAction({ order, source });
    const trace = {
      ...traceBase,
      decision,
      reason,
      completionState: "incomplete",
      targetSection: ldsErrorAction.targetSection,
    };
    logSuccessNavGuardTrace(trace);
    logLdsSuccessRouteDecisionTrace(
      buildLdsSuccessRouteDecisionTracePayload({
        source,
        orderId: id,
        targetPath: profilePath,
        paymentMethod: null,
        isBankTransfer: Boolean(order.bank_transfer),
        isLater: Boolean(order.later),
        isProposal,
        isAwaitingBankTransfer: okBankPending,
        completionState: "incomplete",
        orderResponseStatus,
        shouldUseSuccessRoute: false,
        reason,
      })
    );
    return {
      allow: false,
      allowSuccessRoute: false,
      continueFlow: false,
      decision,
      reason,
      completionState: "incomplete",
      targetSection: ldsErrorAction.targetSection,
      trace,
      ldsErrorAction,
    };
  }

  const trace = {
    ...traceBase,
    hasError: false,
    decision,
    reason,
    completionState,
    targetSection: null,
  };
  logSuccessNavGuardTrace(trace);
  logLdsSuccessRouteDecisionTrace(
    buildLdsSuccessRouteDecisionTracePayload({
      source,
      orderId: id,
      targetPath: allowSuccessRouteFlag ? getPackageOrderSuccessPath(id) : profilePath,
      paymentMethod: null,
      isBankTransfer: Boolean(order.bank_transfer),
      isLater: Boolean(order.later),
      isProposal,
      isAwaitingBankTransfer: okBankPending,
      completionState,
      orderResponseStatus,
      shouldUseSuccessRoute: allowSuccessRouteFlag,
      reason,
    })
  );
  return {
    allow: allowSuccessRouteFlag,
    allowSuccessRoute: allowSuccessRouteFlag,
    continueFlow,
    decision,
    reason,
    completionState,
    targetSection: null,
    trace,
    ldsErrorAction: null,
  };
}
